import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Badge } from '../ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Loader2,
  Upload,
  Trash2,
  Building2,
  FileText,
  Briefcase,
  Clock,
  Paperclip,
  Download,
  Printer,
  CreditCard,
  Camera,
  Check,
  X,
  Pencil,
  ClipboardList,
  MoreVertical,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import IdentityEndpoints from '../../endpoints/IdentityEndpoints'
import EventEndpoints from '../../endpoints/EventEndpoints'
import ScheduleEndpoints from '../../endpoints/ScheduleEndpoints'
import AuthEndpoints from '../../endpoints/AuthEndpoints'
import {
  Identity,
  EventProvisioning,
  TermsAcknowledgement,
  AccessLevelProvisioning,
  AccessLevel,
} from '../../model/Identity'
import {
  SimpleEvent,
  SimpleEventWithProvisionings,
  IdentityRole,
  AccreditationLevel,
} from '../../model/Event'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover'
import {
  ShiftSummarySpot,
  ShiftSpotState,
  ShiftSpotPunchClockState,
} from '../../model/EventSchedule'
import { useSessionStore } from '../../stores/useSessionStore'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { Checkbox } from '../ui/checkbox'
import { Switch } from '../ui/switch'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../ui/accordion'
import { ExternalAccessTypes } from '../../common/ExternalAccessTypes'
import { SendEmailRequest } from '../../requests/SendEmailRequest'
import { ScheduleReportRequest } from '../../requests/ScheduleReportRequest'
import { BadgePrintingLogic } from '../../common/BadgePrintingLogic'
import ReportEndpoints from '../../endpoints/ReportEndpoints'
import { IdentityDossierRequest } from '../../requests/report/IdentityDossierRequest'
import AttachmentEndpoints from '../../endpoints/AttachmentEndpoints'
import { File as AttachmentFile } from '../../model/File'
import * as util from '../../common/Util'
import { isEventPast } from '../../lib/eventDates'
import { DateTime } from 'luxon'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { CameraCaptureDialog } from './CameraCaptureDialog'

const tabs = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'events', label: 'Event Provisioning', icon: Calendar },
  { id: 'shifts', label: 'Shift Summary', icon: Clock },
  { id: 'access', label: 'Access Control', icon: Shield },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
]

const formatDate = (date: Date | string | number | undefined | null) => {
  if (!date) return '-'
  try {
    let d: DateTime
    if (typeof date === 'number') d = DateTime.fromMillis(date)
    else if (typeof date === 'string') d = DateTime.fromISO(date)
    else d = DateTime.fromJSDate(date)
    return d.isValid ? d.toFormat('MMM dd, yyyy') : '-'
  } catch {
    return '-'
  }
}

export const IdentityProfileModern: React.FC = () => {
  const { identityId } = useParams<{ identityId: string }>()
  const navigate = useNavigate()
  const { hasAdmin, hasIdentityManagerFor, hasIdentityManagerForAnyone } =
    useSessionStore()
  const session = useSessionStore((s) => s.session)
  const latestToken = useSessionStore((s) => s.latestToken)
  const masqueradeAs = useSessionStore((s) => s.masqueradeAs)
  const { clients } = useApplicationStore()

  // Derive masquerade state from the JWT directly (source of truth)
  const isMasquerading = useMemo(() => {
    if (!latestToken) return false
    try {
      const decoded = jwtDecode<any>(latestToken)
      return !!decoded?.actualRequesterId
    } catch {
      return false
    }
  }, [latestToken])

  const [identity, setIdentity] = useState<Identity | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false)
  const [masqueradeDialogOpen, setMasqueradeDialogOpen] = useState(false)
  const [dossierDialogOpen, setDossierDialogOpen] = useState(false)
  const [convertGlobalDialogOpen, setConvertGlobalDialogOpen] = useState(false)
  const [dossierViewerOpen, setDossierViewerOpen] = useState(false)
  const [dossierHtml, setDossierHtml] = useState('')
  const [dossierLoading, setDossierLoading] = useState(false)
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false)

  const handleCameraSnap = async (dataUrl: string) => {
    if (!identityId) return
    // dataUrl is "data:image/jpeg;base64,..."
    const base64 = dataUrl.split(',')[1]
    const contentType = dataUrl.split(';')[0].split(':')[1]
    try {
      const updated = await IdentityEndpoints.editIdentity(
        parseInt(identityId),
        {
          field: 'picture',
          value: JSON.stringify({ base64Image: base64, contentType }),
        }
      )
      setIdentity(updated)
      toast.success('Photo updated')
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save photo'))
    }
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const load = async () => {
      if (!identityId) return
      setLoading(true)
      try {
        const data = await IdentityEndpoints.getIdentity(parseInt(identityId))
        setIdentity(data)
      } catch (error) {
        console.error('Failed to load identity:', error)
        toast.error(extractErrorMessage(error, 'Failed to load identity'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [identityId])

  const updateField = async (field: string, value: string) => {
    if (!identityId) return
    try {
      const updated = await IdentityEndpoints.editIdentity(
        parseInt(identityId),
        { field, value: typeof value === 'string' ? value.trim() : value }
      )
      setIdentity(updated)
      toast.success(`Updated ${field}`)
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || error?.message || 'Update failed'
      toast.error(msg)
    }
  }

  const handlePictureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async () => {
      const result = reader.result as string
      const base64 = result.split(',')[1]
      try {
        const updated = await IdentityEndpoints.editIdentity(
          parseInt(identityId!),
          {
            field: 'picture',
            value: JSON.stringify({
              base64Image: base64,
              contentType: file.type,
            }),
          }
        )
        setIdentity(updated)
        toast.success('Photo updated')
      } catch (error: any) {
        toast.error(extractErrorMessage(error, 'Failed to upload photo'))
      }
    }
    reader.readAsDataURL(file)
  }

  const handleDeleteAccessProvisioning = async (clientId: number) => {
    if (!identityId) return
    try {
      const updated = await IdentityEndpoints.deleteClientProvisioning(
        parseInt(identityId),
        clientId
      )
      setIdentity(updated)
      toast.success('Access provisioning removed')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to remove access provisioning'))
    }
  }

  const handleDelete = async () => {
    if (!identityId) return
    setDeleteDialogOpen(false)
    try {
      await IdentityEndpoints.deleteIdentity(parseInt(identityId))
      toast.success('Identity deleted')
      navigate('/identity')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to delete identity'))
    }
  }

  const handleResetPassword = async () => {
    if (!identityId) return
    setResetPasswordDialogOpen(false)
    try {
      await AuthEndpoints.resetIdentityPassword(parseInt(identityId))
      toast.success('Password reset')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to reset password'))
    }
  }

  const printDossier = async (includeDob: boolean) => {
    if (!identityId) return
    setDossierLoading(true)
    try {
      const request: IdentityDossierRequest = {
        identityId: parseInt(identityId),
        rootUrl: location.origin + '/ems/',
        includeDOBSIN: includeDob ? 'true' : 'false',
      }
      const res = await ReportEndpoints.getIdentityDossier(request)
      setDossierHtml(res.data)
      setDossierViewerOpen(true)
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to generate dossier'))
    } finally {
      setDossierLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              Loading identity...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!identity) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-16">
          <p className="text-muted-foreground">Identity not found</p>
        </div>
      </div>
    )
  }

  const isAdmin = hasAdmin()
  const canEdit =
    isAdmin ||
    (identity.parentCustomer
      ? hasIdentityManagerFor(identity.parentCustomer.id)
      : hasIdentityManagerForAnyone())
  const fullName = `${identity.firstName} ${identity.lastName}`

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/identity">Identities</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">
              {fullName}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-4">
              {/* Avatar / Photo */}
              <div className="flex shrink-0 items-center gap-3">
                <div
                  className="relative group shrink-0 cursor-pointer"
                  onClick={() => canEdit && fileInputRef.current?.click()}
                >
                  {identity.picture ? (
                    <img
                      src={`data:${identity.picture.contentType};base64,${identity.picture.data}`}
                      alt={fullName}
                      className="h-16 w-16 max-w-none shrink-0 rounded-full object-cover border-2 border-border"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary border-2 border-border">
                      {(identity.firstName?.[0] || '').toUpperCase()}
                      {(identity.lastName?.[0] || '').toUpperCase()}
                    </div>
                  )}
                  {canEdit && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Upload className="h-5 w-5 text-white" />
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePictureUpload}
                    className="hidden"
                  />
                </div>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title="Take Photo"
                    onClick={() => setCameraDialogOpen(true)}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="min-w-0">
                <CardTitle className="text-2xl break-words">{fullName}</CardTitle>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                  {identity.email && (
                    <span className="flex min-w-0 max-w-full items-center gap-1">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{identity.email}</span>
                    </span>
                  )}
                  {identity.parentCustomer && (
                    <Badge variant="secondary">
                      {identity.parentCustomer.name}
                    </Badge>
                  )}
                  {!identity.parentCustomer && (
                    <Badge variant="outline">Global</Badge>
                  )}
                  {identity.pending ? (
                    <Badge
                      variant="secondary"
                      className="bg-amber-100 text-amber-800"
                    >
                      Pending Approval
                    </Badge>
                  ) : identity.dateArchived ? (
                    <Badge
                      variant="secondary"
                      className="bg-yellow-100 text-yellow-800"
                    >
                      Archived
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="bg-green-50 text-green-700"
                    >
                      Active
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                title="Print Dossier"
                onClick={() => {
                  if (isAdmin) {
                    setDossierDialogOpen(true)
                  } else {
                    printDossier(false)
                  }
                }}
              >
                <Printer className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Print Dossier</span>
              </Button>
              {identity.parentCustomer ? (
                <Button
                  variant="outline"
                  disabled={!identity.parentCustomer.clientBadgesEnabled}
                  title={!identity.parentCustomer.clientBadgesEnabled ? 'Client badges are not enabled for this client' : 'Print Client Badge'}
                  onClick={() => {
                    BadgePrintingLogic(() =>
                      IdentityEndpoints.getBadge(
                        parseInt(identityId),
                        undefined,
                        identity.parentCustomer.id
                      )
                    ).catch((error) => toast.error(extractErrorMessage(error, 'Failed to print client badge')))
                  }}
                >
                  <CreditCard className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Print Client Badge</span>
                </Button>
              ) : (
                clients.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" title="Print Client Badge">
                        <CreditCard className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Print Client Badge</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="max-h-64 overflow-y-auto"
                    >
                      {clients
                        .filter((c) => !c.dateArchived)
                        .map((client) => (
                          <DropdownMenuItem
                            key={client.id}
                            disabled={!client.clientBadgesEnabled}
                            onClick={() => {
                              if (!client.clientBadgesEnabled) return
                              BadgePrintingLogic(() =>
                                IdentityEndpoints.getBadge(
                                  parseInt(identityId),
                                  undefined,
                                  client.id
                                )
                              ).catch((error) =>
                                toast.error(extractErrorMessage(error, 'Failed to print client badge'))
                              )
                            }}
                          >
                            <span className={!client.clientBadgesEnabled ? 'text-muted-foreground' : ''}>
                              {client.name}
                            </span>
                            {!client.clientBadgesEnabled && (
                              <span className="text-[10px] text-muted-foreground ml-1">(no badges)</span>
                            )}
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}
              {canEdit && identity.pending && (
                <Button
                  onClick={async () => {
                    try {
                      await IdentityEndpoints.approvePendingIdentity(
                        identity.id
                      )
                      const updated = await IdentityEndpoints.getIdentity(
                        identity.id
                      )
                      setIdentity(updated)
                      toast.success(`${fullName} has been approved`)
                    } catch (err: any) {
                      toast.error(extractErrorMessage(err, 'Failed to approve identity'))
                    }
                  }}
                >
                  Approve Identity
                </Button>
              )}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" title="More actions">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!identity.pending && (
                      <DropdownMenuItem
                        onClick={() =>
                          updateField(
                            'archived',
                            identity.dateArchived ? 'false' : 'true'
                          )
                        }
                        className={
                          !identity.dateArchived
                            ? 'text-destructive focus:text-destructive'
                            : ''
                        }
                      >
                        {identity.dateArchived ? 'Unarchive' : 'Archive'}
                      </DropdownMenuItem>
                    )}
                    {identity.parentCustomer && (
                      <DropdownMenuItem
                        onClick={() => setConvertGlobalDialogOpen(true)}
                      >
                        Convert to Global
                      </DropdownMenuItem>
                    )}
                    {!identity.admin &&
                      !identity.dateArchived &&
                      identity.ableToLogin &&
                      !isMasquerading &&
                      identity.id !== session.id && (
                        <DropdownMenuItem
                          onClick={() => setMasqueradeDialogOpen(true)}
                        >
                          Masquerade
                        </DropdownMenuItem>
                      )}
                    {!identity.pending && (
                      <DropdownMenuItem
                        onClick={() => setResetPasswordDialogOpen(true)}
                      >
                        Reset Password
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteDialogOpen(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Pending Banner */}
      {identity.pending && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Clock className="h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                This identity is pending approval
              </p>
              <p className="text-xs text-amber-700">
                They were submitted via the public accreditation form and need
                to be reviewed before becoming active.
              </p>
            </div>
          </div>
          {canEdit && (
            <Button
              size="sm"
              onClick={async () => {
                try {
                  await IdentityEndpoints.approvePendingIdentity(identity.id)
                  const updated = await IdentityEndpoints.getIdentity(
                    identity.id
                  )
                  setIdentity(updated)
                  toast.success(`${fullName} has been approved`)
                } catch (err: any) {
                  toast.error(extractErrorMessage(err, 'Failed to approve identity'))
                }
              }}
            >
              Approve
            </Button>
          )}
        </div>
      )}

      {/* Tab Nav */}
      <div className="border-b">
        <nav className="flex gap-1 overflow-x-auto pb-px" role="tablist">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            if (tab.id === 'access' && !isAdmin) return null
            if (tab.id === 'attachments' && !isAdmin) return null
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          identity={identity}
          canEdit={canEdit}
          onUpdate={updateField}
          identityId={parseInt(identityId!)}
        />
      )}
      {activeTab === 'events' && (
        <EventProvisioningTab
          identity={identity}
          identityId={parseInt(identityId!)}
          clients={clients}
          setIdentity={setIdentity}
        />
      )}
      {activeTab === 'shifts' && (
        <ShiftSummaryTab identityId={parseInt(identityId!)} identity={identity} />
      )}
      {activeTab === 'access' && isAdmin && (
        <AccessControlTab
          identity={identity}
          clients={clients}
          onUpdate={updateField}
          onDeleteAccess={handleDeleteAccessProvisioning}
          identityId={parseInt(identityId!)}
          setIdentity={setIdentity}
        />
      )}
      {activeTab === 'attachments' && isAdmin && (
        <AttachmentsTab identity={identity} setIdentity={setIdentity} />
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Identity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete {fullName}? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert to Global Dialog */}
      <AlertDialog
        open={convertGlobalDialogOpen}
        onOpenChange={setConvertGlobalDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert to Global</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to convert {fullName} to a global identity?
              They will no longer belong to{' '}
              {identity.parentCustomer?.name || 'their current client'} and will
              become visible across all clients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await updateField('clientId', '0')
                  const updated = await IdentityEndpoints.getIdentity(
                    identity.id
                  )
                  setIdentity(updated)
                  toast.success('Identity converted to global')
                } catch (err: any) {
                  toast.error(extractErrorMessage(err, 'Failed to convert to global'))
                }
              }}
            >
              Convert to Global
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <AlertDialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset {fullName}'s password? They will not
              be able to log in until setting a new password.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPassword}>
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Masquerade Dialog */}
      <AlertDialog
        open={masqueradeDialogOpen}
        onOpenChange={setMasqueradeDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Masquerade as {fullName}?</AlertDialogTitle>
            <AlertDialogDescription>
              You will see the app exactly as {fullName} sees it. Every action
              you take will be audited and tagged as performed via masquerade.
              This affects all your open tabs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await masqueradeAs(identity.id)
                } catch (error) {
                  toast.error(extractErrorMessage(error, 'Failed to start masquerade'))
                  setMasqueradeDialogOpen(false)
                }
              }}
            >
              Masquerade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dossier DOB Dialog */}
      <AlertDialog open={dossierDialogOpen} onOpenChange={setDossierDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Print Dossier</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to include the date of birth on the dossier?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={dossierLoading}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              disabled={dossierLoading}
              onClick={() => {
                setDossierDialogOpen(false)
                printDossier(false)
              }}
            >
              {dossierLoading ? 'Loading...' : 'No, exclude DOB'}
            </Button>
            <Button
              disabled={dossierLoading}
              onClick={() => {
                setDossierDialogOpen(false)
                printDossier(true)
              }}
            >
              {dossierLoading ? 'Loading...' : 'Yes, include DOB'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dossier Viewer Modal */}
      <Dialog open={dossierViewerOpen} onOpenChange={setDossierViewerOpen}>
        <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Identity Dossier
            </DialogTitle>
            <DialogDescription>{fullName}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 px-6">
            <iframe
              srcDoc={dossierHtml}
              className="w-full h-full border rounded-lg bg-white"
              title="Dossier Preview"
            />
          </div>
          <DialogFooter className="px-6 pb-6 pt-3">
            <Button
              variant="outline"
              onClick={() => setDossierViewerOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                const iframe = document.querySelector(
                  'iframe[title="Dossier Preview"]'
                ) as HTMLIFrameElement
                if (iframe?.contentWindow) {
                  iframe.contentWindow.print()
                }
              }}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera Dialog */}
      <CameraCaptureDialog
        open={cameraDialogOpen}
        onOpenChange={setCameraDialogOpen}
        onSnap={handleCameraSnap}
      />
    </div>
  )
}

// ─── Client Selector ─────────────────────────────────────────────────────

interface ClientSelectorProps {
  identity: Identity
  onUpdate: (field: string, value: string) => Promise<void>
  canEdit: boolean
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  identity,
  onUpdate,
  canEdit,
}) => {
  const { clients } = useApplicationStore()
  const [editing, setEditing] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [saving, setSaving] = useState(false)

  const activeClients = clients.filter((c) => !c.dateArchived)
  const currentClientName =
    identity.parentCustomer?.name || 'Global (No Client)'

  const startEditing = () => {
    setSelectedClientId(
      identity.parentCustomer ? identity.parentCustomer.id.toString() : '0'
    )
    setEditing(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onUpdate('clientId', selectedClientId)
      // Reload identity to get updated parentCustomer
      const updated = await IdentityEndpoints.getIdentity(identity.id)
      // We can't call setIdentity from here, but onUpdate already patches and the parent reloads
      setEditing(false)
      toast.success('Client updated')
      // Force a page reload to reflect the change in the header
      window.location.reload()
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update client'))
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    setEditing(false)
  }

  if (!canEdit) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Client</Label>
        <p className="text-sm h-9 flex items-center px-3 rounded-md border bg-muted/30 text-muted-foreground">
          {currentClientName}
        </p>
      </div>
    )
  }

  if (!editing) {
    return (
      <div className="space-y-2 group">
        <Label className="text-xs text-muted-foreground">Client</Label>
        <div
          className="text-sm min-h-[36px] flex items-center px-3 rounded-md border border-transparent hover:border-border hover:bg-muted/20 cursor-pointer transition-colors"
          onClick={startEditing}
        >
          <span className="flex-1">
            {identity.parentCustomer ? (
              <Badge variant="secondary" className="text-xs">
                {identity.parentCustomer.name}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                Global
              </Badge>
            )}
          </span>
          <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Client</Label>
      <div className="flex gap-1">
        <Select
          value={selectedClientId}
          onValueChange={setSelectedClientId}
          disabled={saving}
        >
          <SelectTrigger className="flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Global (No Client)</SelectItem>
            {activeClients.map((c) => (
              <SelectItem key={c.id} value={c.id.toString()}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-green-600 hover:text-green-700 hover:bg-green-50"
          onClick={handleSave}
          disabled={saving}
          title="Save"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive hover:bg-red-50"
          onClick={handleDiscard}
          disabled={saving}
          title="Discard"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// ─── Region Selector ─────────────────────────────────────────────────────

interface RegionSelectorProps {
  identity: Identity
  onUpdate: (field: string, value: string) => Promise<void>
  canEdit: boolean
}

const RegionSelector: React.FC<RegionSelectorProps> = ({
  identity,
  onUpdate,
  canEdit,
}) => {
  const { regions } = useApplicationStore()
  const [editing, setEditing] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [saving, setSaving] = useState(false)

  const currentIds = new Set((identity.regions || []).map((r) => r.id))

  const startEditing = () => {
    setSelectedIds(new Set(currentIds))
    setEditing(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onUpdate('regions', [...selectedIds].join(','))
      setEditing(false)
    } catch {
      // stay in edit mode
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    setSelectedIds(new Set(currentIds))
    setEditing(false)
  }

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (!canEdit) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Work Regions</Label>
        <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 rounded-md border bg-muted/30">
          {identity.regions && identity.regions.length > 0 ? (
            identity.regions.map((r) => (
              <Badge key={r.id} variant="secondary" className="text-xs">
                {r.name}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground italic">None</span>
          )}
        </div>
      </div>
    )
  }

  if (!editing) {
    return (
      <div className="space-y-2 group">
        <Label className="text-xs text-muted-foreground">Work Regions</Label>
        <div
          className="flex flex-wrap gap-1.5 min-h-[36px] items-center px-3 rounded-md border border-transparent hover:border-border hover:bg-muted/20 cursor-pointer transition-colors"
          onClick={startEditing}
        >
          {identity.regions && identity.regions.length > 0 ? (
            identity.regions.map((r) => (
              <Badge key={r.id} variant="secondary" className="text-xs">
                {r.name}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground italic">
              Click to edit
            </span>
          )}
          <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-auto shrink-0" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Work Regions</Label>
      <div className="flex flex-wrap gap-1.5 p-2 rounded-md border bg-muted/10 min-h-[36px]">
        {regions.map((r) => {
          const isSelected = selectedIds.has(r.id)
          return (
            <Badge
              key={r.id}
              variant={isSelected ? 'default' : 'outline'}
              className="text-xs cursor-pointer select-none"
              onClick={() => !saving && toggle(r.id)}
            >
              {r.name}
              {isSelected && <X className="h-3 w-3 ml-1" />}
            </Badge>
          )
        })}
      </div>
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
          onClick={handleSave}
          disabled={saving}
        >
          <Check className="h-3.5 w-3.5 mr-1" />
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground hover:text-destructive hover:bg-red-50"
          onClick={handleDiscard}
          disabled={saving}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Discard
        </Button>
      </div>
    </div>
  )
}

// ─── Editable Field ──────────────────────────────────────────────────────

interface EditableFieldProps {
  label: string
  value: string
  onSave: (value: string) => Promise<void>
  disabled?: boolean
  type?: string
  placeholder?: string
  multiline?: boolean
  rows?: number
  className?: string
}

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  onSave,
  disabled = false,
  type = 'text',
  placeholder = '',
  multiline = false,
  rows = 3,
  className = '',
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [editing])

  const handleSave = async () => {
    const trimmed = draft.trim()
    if (trimmed === value) {
      setDraft(trimmed)
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(trimmed)
      setDraft(trimmed)
      setEditing(false)
    } catch {
      // keep editing on error
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    setDraft(value)
    setEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) handleSave()
    if (e.key === 'Escape') handleDiscard()
  }

  if (disabled) {
    return (
      <div className={cn('space-y-2', className)}>
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <p className="text-sm h-9 flex items-center px-3 rounded-md border bg-muted/30 text-muted-foreground">
          {value || <span className="italic">-</span>}
        </p>
      </div>
    )
  }

  if (!editing) {
    return (
      <div className={cn('space-y-2 group', className)}>
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <div
          className="text-sm min-h-[36px] flex items-center px-3 rounded-md border border-transparent hover:border-border hover:bg-muted/20 cursor-pointer transition-colors"
          onClick={() => setEditing(true)}
        >
          <span className="flex-1">
            {value || (
              <span className="text-muted-foreground italic">
                {placeholder || 'Click to edit'}
              </span>
            )}
          </span>
          <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0" />
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-1">
        {multiline ? (
          <Textarea
            ref={inputRef as React.Ref<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            rows={rows}
            placeholder={placeholder}
            className="flex-1"
          />
        ) : (
          <Input
            ref={inputRef as React.Ref<HTMLInputElement>}
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            placeholder={placeholder}
            className="flex-1"
          />
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-green-600 hover:text-green-700 hover:bg-green-50"
          onClick={handleSave}
          disabled={saving}
          title="Save"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive hover:bg-red-50"
          onClick={handleDiscard}
          disabled={saving}
          title="Discard"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// ─── Overview Tab ────────────────────────────────────────────────────────

interface OverviewTabProps {
  identity: Identity
  canEdit: boolean
  onUpdate: (field: string, value: string) => Promise<void>
  identityId: number
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  identity,
  canEdit,
  onUpdate,
  identityId,
}) => {
  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <EditableField
              label="First Name"
              value={identity.firstName || ''}
              onSave={(v) => onUpdate('firstName', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Middle Name"
              value={identity.middleName || ''}
              onSave={(v) => onUpdate('middleName', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Last Name"
              value={identity.lastName || ''}
              onSave={(v) => onUpdate('lastName', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Email"
              value={identity.email || ''}
              onSave={(v) => onUpdate('email', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Company Name"
              value={identity.companyName || ''}
              onSave={(v) => onUpdate('companyName', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Employee Number"
              value={identity.employeeNumber || ''}
              onSave={(v) => onUpdate('employeeNumber', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Pay Rate"
              value={identity.payRate || ''}
              onSave={(v) => onUpdate('payRate', v)}
              disabled={!canEdit}
            />
            {canEdit && (
              <EditableField
                label="Date of Birth"
                type="date"
                value={
                  identity.dateOfBirth
                    ? DateTime.fromJSDate(
                        new Date(identity.dateOfBirth)
                      ).toFormat('yyyy-MM-dd')
                    : ''
                }
                onSave={(v) => onUpdate('dateOfBirth', v)}
              />
            )}
            <EditableField
              label="Client Badge Role Name"
              value={identity.clientBadgeRoleName || ''}
              onSave={(v) => onUpdate('clientBadgeRoleName', v)}
              disabled={!canEdit}
              placeholder="Override badge role text"
            />
            <ClientSelector
              identity={identity}
              onUpdate={onUpdate}
              canEdit={canEdit}
            />
            <RegionSelector
              identity={identity}
              onUpdate={onUpdate}
              canEdit={canEdit}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Phone className="h-5 w-5" />
            Contact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EditableField
              label="Home Phone"
              value={identity.homePhoneNumber || ''}
              onSave={(v) => onUpdate('homePhoneNumber', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Mobile Phone"
              value={identity.mobilePhoneNumber || ''}
              onSave={(v) => onUpdate('mobilePhoneNumber', v)}
              disabled={!canEdit}
            />
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5" />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EditableField
              label="Street Address"
              value={identity.streetAddress || ''}
              onSave={(v) => onUpdate('streetAddress', v)}
              disabled={!canEdit}
              className="md:col-span-2"
            />
            <EditableField
              label="City"
              value={identity.city || ''}
              onSave={(v) => onUpdate('city', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Province"
              value={identity.province || ''}
              onSave={(v) => onUpdate('province', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Postal Code"
              value={identity.postalCode || ''}
              onSave={(v) => onUpdate('postalCode', v)}
              disabled={!canEdit}
            />
            <EditableField
              label="Country"
              value={identity.country || ''}
              onSave={(v) => onUpdate('country', v)}
              disabled={!canEdit}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />
            Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <EditableField
            label="Printable Notes"
            value={identity.printableNotes || ''}
            onSave={(v) => onUpdate('printableNotes', v)}
            disabled={!canEdit}
            multiline
            rows={3}
            placeholder="Visible on printed materials"
          />
          {canEdit && (
            <EditableField
              label="Private Notes (Admin Only)"
              value={identity.privateNotes || ''}
              onSave={(v) => onUpdate('privateNotes', v)}
              multiline
              rows={3}
              placeholder="Internal notes, not visible on printouts"
            />
          )}
        </CardContent>
      </Card>

      {/* Metadata (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">ID</p>
              <p className="font-medium">{identity.id}</p>
            </div>
            <div>
              <p className="text-muted-foreground">UUID</p>
              <p className="font-medium font-mono text-xs">{identity.uuid}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Created</p>
              <p className="font-medium">{formatDate(identity.dateCreated)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Modified</p>
              <p className="font-medium">{formatDate(identity.lastModified)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">PIN</p>
              <p className="font-medium font-mono">{identity.pin || '-'}</p>
            </div>
            {identity.createdBy && (
              <div>
                <p className="text-muted-foreground">Created By</p>
                <p className="font-medium">
                  {identity.createdBy.firstName} {identity.createdBy.lastName}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Event Provisioning Tab ──────────────────────────────────────────────

const NONE_ID = -1
const DEFAULT_ACCREDITATION_ID = -1

interface EventProvisioningTabProps {
  identity: Identity
  identityId: number
  clients: any[]
  setIdentity: (identity: Identity) => void
}

const EventProvisioningTab: React.FC<EventProvisioningTabProps> = ({
  identity,
  identityId,
  clients,
  setIdentity,
}) => {
  const [events, setEvents] = useState<SimpleEventWithProvisionings[]>([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [clientFilter, setClientFilter] = useState<string>('')
  const [showPast, setShowPast] = useState(false)

  const activeClients = [...clients.filter((c: any) => !c.dateArchived)].sort(
    (a, b) => a.name.localeCompare(b.name)
  )

  // Provisioning counts per client
  const provisioningsByClient = (identity.eventProvisionings || []).reduce<
    Record<number, number>
  >((acc, p) => {
    const cId = p.provisionedFor.client?.id
    if (cId) acc[cId] = (acc[cId] || 0) + 1
    return acc
  }, {})
  const totalProvisionings = identity.eventProvisionings?.length || 0
  // Clients with provisionings, sorted by count desc then name
  const provisionedClients = Object.entries(provisioningsByClient)
    .map(([cId, count]) => ({
      id: parseInt(cId),
      name: clients.find((c: any) => c.id === parseInt(cId))?.name || 'Unknown',
      count,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))

  // Set default client filter
  useEffect(() => {
    if (identity.parentCustomer) {
      setClientFilter(identity.parentCustomer.id.toString())
      setShowPast(true)
    } else if (activeClients.length > 0 && !clientFilter) {
      setClientFilter(activeClients[0].id.toString())
    }
  }, [identity.parentCustomer, activeClients.length])

  // Load events for the selected client only
  useEffect(() => {
    if (!clientFilter) return
    const load = async () => {
      setLoadingEvents(true)
      try {
        const data = await EventEndpoints.getEventListForProvisioning(parseInt(clientFilter))
        setEvents(data)
      } catch (error) {
        console.error('Failed to load events:', error)
        toast.error(extractErrorMessage(error, 'Failed to load events'))
      } finally {
        setLoadingEvents(false)
      }
    }
    load()
  }, [clientFilter])

  const filteredEvents = events.filter((e) => showPast || !isEventPast(e))

  const sortedEvents = [...filteredEvents].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  const getProvisioning = (eventId: number): EventProvisioning | undefined => {
    return (identity.eventProvisionings || []).find(
      (p) => p.provisionedFor.id === eventId
    )
  }

  const getTermsAcknowledgement = (
    eventId: number
  ): TermsAcknowledgement | undefined => {
    return (identity.termsAcknowledgements || []).find(
      (a) => a.eventId === eventId
    )
  }

  const hasNameMismatch = (ack: TermsAcknowledgement): boolean => {
    const normalize = (s: string | null | undefined) =>
      (s || '').trim().toLowerCase()
    return (
      normalize(ack.submittedFirstName) !== normalize(identity.firstName) ||
      normalize(ack.submittedLastName) !== normalize(identity.lastName)
    )
  }

  const handleRoleChange = async (
    event: SimpleEventWithProvisionings,
    roleIdStr: string
  ) => {
    const roleId = parseInt(roleIdStr)
    const existing = getProvisioning(event.id)

    try {
      if (roleId === NONE_ID) {
        const updated = await IdentityEndpoints.deleteEventProvisioning(
          identityId,
          event.id
        )
        setIdentity(updated)
        toast.success(`Removed from ${event.name}`)
      } else {
        const updated = await IdentityEndpoints.provisionForEvent(
          identityId,
          event.id,
          {
            roleId,
            secondaryRoleName: existing?.secondaryRoleName || '',
            accreditationLevelId: existing?.accreditationLevel?.id ?? null,
          }
        )
        setIdentity(updated)
        toast.success(`Provisioned to ${event.name}`)
      }
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update provisioning'))
    }
  }

  const handleAccreditationChange = async (
    event: SimpleEventWithProvisionings,
    accIdStr: string
  ) => {
    const accId = parseInt(accIdStr)
    const existing = getProvisioning(event.id)
    if (!existing) return

    try {
      const updated = await IdentityEndpoints.provisionForEvent(
        identityId,
        event.id,
        {
          roleId: existing.role.id,
          secondaryRoleName: existing.secondaryRoleName,
          accreditationLevelId:
            accId === DEFAULT_ACCREDITATION_ID ? null : accId,
        }
      )
      setIdentity(updated)
      toast.success('Accreditation updated')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update accreditation'))
    }
  }

  const handleSecondaryRoleBlur = async (
    event: SimpleEventWithProvisionings,
    rawValue: string
  ) => {
    const value = (rawValue || '').trim()
    const existing = getProvisioning(event.id)
    if (!existing || existing.secondaryRoleName === value) return

    try {
      const updated = await IdentityEndpoints.provisionForEvent(
        identityId,
        event.id,
        {
          roleId: existing.role.id,
          secondaryRoleName: value,
          accreditationLevelId: existing.accreditationLevel?.id ?? null,
        }
      )
      setIdentity(updated)
      toast.success('Secondary role updated')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update secondary role'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Event Provisioning
            </CardTitle>
            <CardDescription>
              Assign this identity to events with specific roles
            </CardDescription>
          </div>
        </div>
        {/* Filters */}
        <div className="flex items-center gap-4 pt-2">
          {!identity.parentCustomer && (
            <div className="flex-1 max-w-xs">
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {activeClients.map((c: any) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      <span className="flex items-center gap-2">
                        {c.name}
                        {provisioningsByClient[c.id] > 0 && (
                          <Badge
                            variant="secondary"
                            className="text-xs px-1.5 py-0"
                          >
                            {provisioningsByClient[c.id]}
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showPastEvents"
              checked={showPast}
              onCheckedChange={(checked) => setShowPast(checked === true)}
            />
            <label
              htmlFor="showPastEvents"
              className="text-sm font-medium whitespace-nowrap"
            >
              Show past events
            </label>
          </div>
        </div>
        {/* Provisioning summary — clickable chips (only for global identities) */}
        {!identity.parentCustomer && totalProvisionings > 0 && (
          <div className="pt-2 space-y-1.5">
            <p className="text-xs text-muted-foreground">
              {totalProvisionings} provisioning
              {totalProvisionings !== 1 ? 's' : ''} across{' '}
              {provisionedClients.length} client
              {provisionedClients.length !== 1 ? 's' : ''}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {provisionedClients.map((pc) => {
                const isActive = clientFilter === pc.id.toString()
                return (
                  <button
                    key={pc.id}
                    onClick={() => setClientFilter(pc.id.toString())}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors cursor-pointer border ${
                      isActive
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {pc.name}
                    <span
                      className={`rounded-full px-1.5 py-0 text-[10px] ${isActive ? 'bg-primary-foreground/20' : 'bg-muted-foreground/20'}`}
                    >
                      {pc.count}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loadingEvents ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              Loading events...
            </p>
          </div>
        ) : sortedEvents.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {clientFilter
                ? 'No events found for this client'
                : 'Select a client to see events'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[20%]">Event</TableHead>
                <TableHead className="w-[25%]">Role</TableHead>
                <TableHead className="w-[20%]">Accreditation</TableHead>
                <TableHead className="w-[20%]">Secondary Role</TableHead>
                <TableHead className="w-[15%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEvents.map((event) => {
                const provisioning = getProvisioning(event.id)
                const acknowledgement = getTermsAcknowledgement(event.id)
                const isPast = isEventPast(event)
                const sortedRoles = [...event.roles].sort((a, b) =>
                  a.internalName.localeCompare(b.internalName)
                )
                const schedulableRoles = sortedRoles.filter(
                  (r) => r.schedulable
                )
                const nonSchedulableRoles = sortedRoles.filter(
                  (r) => !r.schedulable
                )
                const accreditationLevels = [...event.accreditationLevels].sort(
                  (a, b) => a.name.localeCompare(b.name)
                )

                return (
                  <TableRow
                    key={event.id}
                    className={isPast ? 'opacity-60' : ''}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{event.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(event.startDate)} -{' '}
                          {formatDate(event.endDate)}
                        </p>
                        {acknowledgement && (
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            <Badge
                              variant="outline"
                              className="text-xs text-green-700 border-green-300 bg-green-50"
                              title={`Terms & conditions agreed ${formatDate(acknowledgement.dateAcknowledged)} as "${acknowledgement.submittedFirstName} ${acknowledgement.submittedLastName}" (${acknowledgement.submittedEmail || 'no email'})${acknowledgement.termsVersion ? `, version ${acknowledgement.termsVersion}` : ''}`}
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              T&C {formatDate(acknowledgement.dateAcknowledged)}
                            </Badge>
                            {hasNameMismatch(acknowledgement) && (
                              <Badge
                                variant="outline"
                                className="text-xs text-amber-700 border-amber-300 bg-amber-50"
                                title={`Submitted as "${acknowledgement.submittedFirstName} ${acknowledgement.submittedLastName}" but this profile is "${identity.firstName} ${identity.lastName}" — verify this is the same person.`}
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Name mismatch:{' '}
                                {acknowledgement.submittedFirstName}{' '}
                                {acknowledgement.submittedLastName}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <select
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={provisioning ? provisioning.role.id : NONE_ID}
                        onChange={(e) =>
                          handleRoleChange(event, e.target.value)
                        }
                        disabled={isPast}
                      >
                        <option value={NONE_ID}>Not Provisioned</option>
                        {schedulableRoles.length > 0 && (
                          <optgroup label="Schedulable">
                            {schedulableRoles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.internalName}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {nonSchedulableRoles.length > 0 && (
                          <optgroup label="Non-Schedulable">
                            {nonSchedulableRoles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.internalName}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </TableCell>
                    <TableCell>
                      {provisioning && (
                        <select
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                          value={
                            provisioning.accreditationLevel?.id ??
                            DEFAULT_ACCREDITATION_ID
                          }
                          onChange={(e) =>
                            handleAccreditationChange(event, e.target.value)
                          }
                          disabled={isPast}
                        >
                          <option value={DEFAULT_ACCREDITATION_ID}>
                            Role Default
                          </option>
                          {accreditationLevels.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </TableCell>
                    <TableCell>
                      {provisioning && (
                        <Input
                          className="h-9"
                          defaultValue={provisioning.secondaryRoleName || ''}
                          onBlur={(e) =>
                            handleSecondaryRoleBlur(event, e.target.value)
                          }
                          disabled={isPast}
                          placeholder="Optional"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {provisioning && (
                        <div className="flex items-center justify-end gap-1">
                          {provisioning.role.schedulable && (
                            <Badge variant="outline" className="text-xs">
                              <Calendar className="h-3 w-3 mr-1" />
                              Sched.
                            </Badge>
                          )}
                          <Link
                            to={`/event/${event.id}/provision`}
                            onClick={(e) => e.stopPropagation()}
                            title="Go to Provisioning"
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                          {provisioning.role.schedulable && (
                            <Link
                              to={`/event/${event.id}/schedule`}
                              onClick={(e) => e.stopPropagation()}
                              title="Go to Schedule"
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                              >
                                <Calendar className="h-3.5 w-3.5" />
                              </Button>
                            </Link>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Print Badge"
                            onClick={(e) => {
                              e.stopPropagation()
                              BadgePrintingLogic(() =>
                                IdentityEndpoints.getBadge(identityId, event.id)
                              ).catch((error) =>
                                toast.error(extractErrorMessage(error, 'Failed to print badge'))
                              )
                            }}
                          >
                            <Printer className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Shift Summary Tab ──────────────────────────────────────────────────

interface ShiftSummaryTabProps {
  identityId: number
  identity: Identity
}

const ShiftSummaryTab: React.FC<ShiftSummaryTabProps> = ({
  identityId,
  identity,
}) => {
  const [shifts, setShifts] = useState<ShiftSummarySpot[]>([])
  const [loading, setLoading] = useState(true)
  const [showPast, setShowPast] = useState(false)
  const [eventFilter, setEventFilter] = useState<string>('all')
  const [selectedShift, setSelectedShift] = useState<ShiftSummarySpot | null>(
    null
  )
  const [detailOpen, setDetailOpen] = useState(false)
  const [unassignConfirmOpen, setUnassignConfirmOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [pendingBulkUnassign, setPendingBulkUnassign] = useState<
    ShiftSummarySpot[] | null
  >(null)
  const [bulkUnassignConfirmText, setBulkUnassignConfirmText] = useState('')
  const [unassigning, setUnassigning] = useState(false)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [emailSending, setEmailSending] = useState(false)

  const loadShifts = () => {
    setLoading(true)
    IdentityEndpoints.getShiftsForIdentity(identityId)
      .then((data) => {
        setShifts(data)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching shifts:', err)
        toast.error(extractErrorMessage(err, 'Failed to load shift summary'))
        setLoading(false)
      })
  }

  useEffect(() => {
    loadShifts()
  }, [identityId])

  const parseDate = (val: string | number): DateTime => {
    if (typeof val === 'number' || /^\d+$/.test(String(val))) {
      return DateTime.fromMillis(Number(val))
    }
    return DateTime.fromISO(String(val))
  }

  // Derive unique events from shift data
  const eventOptions = React.useMemo(() => {
    const map = new Map<number, string>()
    shifts.forEach((s) => {
      if (!map.has(s.eventId)) map.set(s.eventId, s.eventName)
    })
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [shifts])

  const now = new Date()
  const filteredShifts =
    eventFilter === 'all'
      ? shifts
      : shifts.filter((s) => s.eventId === parseInt(eventFilter))
  const upcomingShifts = filteredShifts.filter(
    (s) => parseDate(s.endTime).toJSDate() >= now
  )
  const pastShifts = filteredShifts.filter(
    (s) => parseDate(s.endTime).toJSDate() < now
  )
  const displayedShifts = showPast ? pastShifts : upcomingShifts

  const handleEmailSchedule = async () => {
    if (!emailTo.trim()) {
      toast.error('Please enter recipient email(s)')
      return
    }

    setEmailSending(true)
    try {
      const targetEventId =
        eventFilter !== 'all'
          ? parseInt(eventFilter)
          : eventOptions.length === 1
            ? eventOptions[0].id
            : null

      if (!targetEventId) {
        toast.error('Please select a specific event first')
        setEmailSending(false)
        return
      }

      const spotsToEmail = upcomingShifts
        .filter((s) => s.eventId === targetEventId)
        .map((s) => s.id)

      if (spotsToEmail.length === 0) {
        toast.error('No upcoming shifts to email')
        setEmailSending(false)
        return
      }

      const reportRes = await ReportEndpoints.getScheduleReport({
        eventId: targetEventId,
        spotIds: spotsToEmail,
        location: 'All',
        position: 'All',
        fromDate: DateTime.now().toFormat('yyyy-MM-dd'),
        toDate: DateTime.now().plus({ years: 1 }).toFormat('yyyy-MM-dd'),
        employee: identity.firstName + ' ' + identity.lastName,
        identityId: identityId,
        spotStatus: 'Show All',
        rootUrl: location.origin + '/ems/',
      })

      await ReportEndpoints.sendReportEmail({
        subject: emailSubject,
        recipients: emailTo.split(',').map((e) => e.trim()),
        content: reportRes.data,
        customMessage: emailMessage,
        signature: reportRes.signature || '',
        clientId: identity.parentCustomer?.id ?? null,
      })

      toast.success('Schedule emailed')
      setEmailDialogOpen(false)
    } catch (err) {
      console.error('Error emailing schedule:', err)
      toast.error(extractErrorMessage(err, 'Failed to email schedule'))
    } finally {
      setEmailSending(false)
    }
  }

  const openEmailDialog = () => {
    const fullName = `${identity.firstName || ''} ${identity.lastName || ''}`.trim()
    setEmailTo(identity.email || '')
    setEmailSubject(`Schedule for ${fullName}`)
    setEmailMessage('')
    setEmailDialogOpen(true)
  }

  const formatShiftDate = (val: string | number) => {
    const d = parseDate(val)
    return d.isValid ? d.toFormat('MMM dd, yyyy') : '-'
  }

  const formatShiftTime = (val: string | number) => {
    const d = parseDate(val)
    return d.isValid ? d.toFormat('h:mm a') : '-'
  }

  const punchClockBadgeClass = (state: ShiftSpotPunchClockState): string => {
    const variants: Record<string, string> = {
      PENDING: 'bg-gray-100 text-gray-700',
      CHECKED_IN: 'bg-green-100 text-green-700',
      BREAK: 'bg-yellow-100 text-yellow-700',
      CHECKED_OUT: 'bg-blue-100 text-blue-700',
      LATE: 'bg-orange-100 text-orange-700',
      MISSED: 'bg-red-100 text-red-700',
      ERROR: 'bg-red-100 text-red-700',
    }
    return variants[state] || ''
  }

  const punchClockLabel = (state: ShiftSpotPunchClockState): string => {
    const label = state.replace(/_/g, ' ')
    return label.charAt(0) + label.slice(1).toLowerCase()
  }

  const handleRowClick = (shift: ShiftSummarySpot) => {
    setSelectedShift(shift)
    setDetailOpen(true)
  }

  const handleUnassign = async () => {
    if (!selectedShift) return
    setUnassigning(true)
    try {
      await ScheduleEndpoints.unassignSpot(selectedShift.id)
      toast.success('Spot unassigned')
      setUnassignConfirmOpen(false)
      setDetailOpen(false)
      setSelectedShift(null)
      loadShifts()
    } catch (err) {
      console.error('Error unassigning spot:', err)
      toast.error(extractErrorMessage(err, 'Failed to unassign spot'))
    } finally {
      setUnassigning(false)
    }
  }

  const assignedDisplayed = displayedShifts.filter(
    (s) => s.state === ShiftSpotState.ASSIGNED
  )
  const selectedAssigned = assignedDisplayed.filter((s) =>
    selectedIds.has(s.id)
  )

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedAssigned.length === assignedDisplayed.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(assignedDisplayed.map((s) => s.id)))
    }
  }

  // Snapshot the rows when the dialog opens so the count the user is asked to
  // type can't shift under them while the batch runs.
  const bulkUnassignRows = pendingBulkUnassign ?? []
  const bulkUnassignCount = bulkUnassignRows.length
  const bulkUnassignDates = bulkUnassignRows
    .map((s) => s.date)
    .sort((a, b) => parseDate(a).toMillis() - parseDate(b).toMillis())
  const bulkUnassignEvents = new Set(bulkUnassignRows.map((s) => s.eventName))
  const bulkUnassignConfirmed =
    bulkUnassignConfirmText.trim() === String(bulkUnassignCount)

  const closeBulkUnassignConfirm = () => {
    setPendingBulkUnassign(null)
    setBulkUnassignConfirmText('')
  }

  const handleBulkUnassign = async () => {
    if (!pendingBulkUnassign || !bulkUnassignConfirmed) return
    const rows = pendingBulkUnassign
    setUnassigning(true)
    try {
      for (const shift of rows) {
        await ScheduleEndpoints.unassignSpot(shift.id)
      }
      toast.success(
        `Unassigned ${rows.length} shift${rows.length !== 1 ? 's' : ''}`
      )
      closeBulkUnassignConfirm()
      setSelectedIds(new Set())
      loadShifts()
    } catch (err) {
      console.error('Error unassigning shifts:', err)
      toast.error(extractErrorMessage(err, 'Failed to unassign some shifts'))
      loadShifts()
    } finally {
      setUnassigning(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">
                {showPast ? 'Past Shifts' : 'Upcoming Shifts'}
              </CardTitle>
              <CardDescription>
                {displayedShifts.length} shift
                {displayedShifts.length !== 1 ? 's' : ''}
                {showPast
                  ? ` | ${upcomingShifts.length} upcoming`
                  : ` | ${pastShifts.length} past`}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {selectedAssigned.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setBulkUnassignConfirmText('')
                    setPendingBulkUnassign(selectedAssigned)
                  }}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  Unassign Selected ({selectedAssigned.length})
                </Button>
              )}
              <span
                title={
                  eventFilter === 'all' && eventOptions.length > 1
                    ? 'Select a specific event to email its schedule'
                    : upcomingShifts.length === 0
                      ? 'No upcoming shifts to email'
                      : undefined
                }
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEmailDialog}
                  disabled={
                    upcomingShifts.length === 0 ||
                    (eventFilter === 'all' && eventOptions.length > 1)
                  }
                >
                  <Mail className="mr-1 h-4 w-4" />
                  Email Schedule
                </Button>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPast(!showPast)
                  setSelectedIds(new Set())
                }}
              >
                {showPast ? 'Show Upcoming' : 'Show Past'}
              </Button>
            </div>
          </div>
          {eventOptions.length > 1 && (
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">
                Filter by event:
              </Label>
              <Select
                value={eventFilter}
                onValueChange={(val) => {
                  setEventFilter(val)
                  setSelectedIds(new Set())
                }}
              >
                <SelectTrigger className="w-[250px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {eventOptions.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id.toString()}>
                      {ev.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {displayedShifts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No {showPast ? 'past' : 'upcoming'} shifts.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {assignedDisplayed.length > 0 && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          assignedDisplayed.length > 0 &&
                          selectedAssigned.length === assignedDisplayed.length
                        }
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                  )}
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedShifts.map((shift) => (
                  <TableRow
                    key={shift.id}
                    className="cursor-pointer"
                    onClick={() => handleRowClick(shift)}
                  >
                    {assignedDisplayed.length > 0 && (
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                      >
                        {shift.state === ShiftSpotState.ASSIGNED ? (
                          <Checkbox
                            checked={selectedIds.has(shift.id)}
                            onCheckedChange={() => toggleSelect(shift.id)}
                          />
                        ) : (
                          <span />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-medium">
                      {shift.eventName}
                    </TableCell>
                    <TableCell>{formatShiftDate(shift.date)}</TableCell>
                    <TableCell>{formatShiftTime(shift.startTime)}</TableCell>
                    <TableCell>{formatShiftTime(shift.endTime)}</TableCell>
                    <TableCell>{shift.location}</TableCell>
                    <TableCell>{shift.position}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          punchClockBadgeClass(shift.punchClockState)
                        )}
                      >
                        {punchClockLabel(shift.punchClockState)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Shift Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Shift Details</DialogTitle>
            <DialogDescription>
              {selectedShift?.eventName} &mdash;{' '}
              {selectedShift && formatShiftDate(selectedShift.date)}
            </DialogDescription>
          </DialogHeader>
          {selectedShift && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Event</span>
                  <p className="font-medium">
                    <Link
                      to={`/event/${selectedShift.eventId}/schedule`}
                      className="text-primary hover:underline"
                      onClick={() => setDetailOpen(false)}
                    >
                      {selectedShift.eventName}
                    </Link>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Date</span>
                  <p className="font-medium">
                    {formatShiftDate(selectedShift.date)}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Start Time</span>
                  <p className="font-medium">
                    {formatShiftTime(selectedShift.startTime)}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">End Time</span>
                  <p className="font-medium">
                    {formatShiftTime(selectedShift.endTime)}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Location</span>
                  <p className="font-medium">{selectedShift.location}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Position</span>
                  <p className="font-medium">{selectedShift.position}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Punch Status</span>
                  <p>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        punchClockBadgeClass(selectedShift.punchClockState)
                      )}
                    >
                      {punchClockLabel(selectedShift.punchClockState)}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Had Break</span>
                  <p className="font-medium">
                    {selectedShift.hadBreak ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
              {selectedShift.shiftNotes && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Shift Notes</span>
                  <p className="mt-1 rounded-md bg-muted p-2 text-sm">
                    {selectedShift.shiftNotes}
                  </p>
                </div>
              )}
              {selectedShift.notes && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Spot Notes</span>
                  <p className="mt-1 rounded-md bg-muted p-2 text-sm">
                    {selectedShift.notes}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:justify-between">
            <div>
              {selectedShift &&
                selectedShift.state === ShiftSpotState.ASSIGNED && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setUnassignConfirmOpen(true)}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Unassign
                  </Button>
                )}
            </div>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign Confirmation */}
      <AlertDialog
        open={unassignConfirmOpen}
        onOpenChange={setUnassignConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unassign from Shift</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unassign this identity from the shift on{' '}
              {selectedShift && formatShiftDate(selectedShift.date)} at{' '}
              {selectedShift?.location}? This will remove their assignment from
              this spot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnassign} disabled={unassigning}>
              {unassigning ? 'Unassigning...' : 'Unassign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Unassign Confirmation */}
      <AlertDialog
        open={pendingBulkUnassign !== null}
        onOpenChange={(open) => {
          if (unassigning || open) return
          closeBulkUnassignConfirm()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Unassign from {bulkUnassignCount} Shift
              {bulkUnassignCount !== 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes {identity?.firstName || 'this identity'}
              {identity?.lastName ? ` ${identity.lastName}` : ''} from{' '}
              {bulkUnassignCount} shift{bulkUnassignCount !== 1 ? 's' : ''}
              {bulkUnassignEvents.size > 1 && (
                <> across {bulkUnassignEvents.size} events</>
              )}
              {bulkUnassignDates.length > 0 && (
                <>
                  {' '}from{' '}
                  {formatShiftDate(bulkUnassignDates[0])}
                  {bulkUnassignDates[0] !==
                    bulkUnassignDates[bulkUnassignDates.length - 1] && (
                    <>
                      {' '}to{' '}
                      {formatShiftDate(
                        bulkUnassignDates[bulkUnassignDates.length - 1]
                      )}
                    </>
                  )}
                </>
              )}
              . This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="bulk-unassign-confirm-count" className="text-sm">
              Type{' '}
              <span className="font-semibold text-foreground">
                {bulkUnassignCount}
              </span>{' '}
              to confirm
            </Label>
            <Input
              id="bulk-unassign-confirm-count"
              value={bulkUnassignConfirmText}
              onChange={(e) => setBulkUnassignConfirmText(e.target.value)}
              placeholder={String(bulkUnassignCount)}
              autoFocus
              autoComplete="off"
              disabled={unassigning}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Keep the dialog mounted while the batch runs so the loading
                // state is visible and a second click can't re-fire it.
                e.preventDefault()
                handleBulkUnassign()
              }}
              disabled={!bulkUnassignConfirmed || unassigning}
            >
              {unassigning
                ? 'Unassigning...'
                : `Unassign ${bulkUnassignCount} Shift${bulkUnassignCount !== 1 ? 's' : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Schedule Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Schedule</DialogTitle>
            <DialogDescription>
              Send {upcomingShifts.length} upcoming shift
              {upcomingShifts.length !== 1 ? 's' : ''}
              {eventFilter !== 'all'
                ? ` for ${eventOptions.find((e) => e.id === parseInt(eventFilter))?.name}`
                : ''}{' '}
              to the specified recipients.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>To</Label>
              <Input
                placeholder="email@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Separate multiple emails with commas
              </p>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                placeholder="Add a custom message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(false)}
              disabled={emailSending}
            >
              Cancel
            </Button>
            <Button onClick={handleEmailSchedule} disabled={emailSending}>
              {emailSending ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-1 h-4 w-4" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ─── Access Control Tab ────────────────────��──────────────────��──────────

// POS config panel sections, grantable one by one (mirrors ConfigSection in authentigate-t1-common
// and POS_CONFIG_SECTIONS in ems-backend). "POS Config Access" already covers every section except
// Maintenance.
const POS_CONFIG_SECTIONS: { value: string; label: string; coveredByConfigAccess: boolean }[] = [
  { value: 'DEBIT_CREDIT', label: 'Debit/Credit (incl. kiosk reader arrow)', coveredByConfigAccess: true },
  { value: 'BOX_OFFICE', label: 'Box-office', coveredByConfigAccess: true },
  { value: 'DEVICE', label: 'Device', coveredByConfigAccess: true },
  { value: 'OFFLINE', label: 'Master Password & Offline Mode', coveredByConfigAccess: true },
  { value: 'TOKEN_PRINTING', label: 'Token Printing', coveredByConfigAccess: true },
  { value: 'MAINTENANCE', label: 'Maintenance', coveredByConfigAccess: false },
]

interface AccessControlTabProps {
  identity: Identity
  clients: any[]
  onUpdate: (field: string, value: string) => Promise<void>
  onDeleteAccess: (clientId: number) => Promise<void>
  identityId: number
  setIdentity: (identity: Identity) => void
}

const accessLevelLabels: Record<string, string> = {
  IDENTITY_MANAGER: 'Identity Manager',
  SCHEDULE_MANAGER: 'Schedule Manager',
  SCHEDULE_ADMIN: 'Schedule Admin',
  SCHEDULE_AND_PUNCH_CLOCK_ADMIN: 'Schedule & Punch Clock Admin',
}

const accessLevelDescriptions: Record<string, string> = {
  IDENTITY_MANAGER: 'Create identities, provision identities for events',
  SCHEDULE_MANAGER: 'Identity Manager + view schedule, assign/unassign shifts, mark shifts as unassignable',
  SCHEDULE_ADMIN: 'Schedule Manager + add shifts, create/modify roles, shifts, and events',
  SCHEDULE_AND_PUNCH_CLOCK_ADMIN: 'Schedule Admin + access punch clock system',
}

/** External access stores its event filter as a comma-separated id list. */
const parseEventIds = (raw: string): number[] =>
  raw
    .split(',')
    .map((part) => parseInt(part.trim(), 10))
    .filter((id) => !isNaN(id))

interface EventFilterPickerProps {
  clientId: number | null
  selectedIds: number[]
  onChange: (ids: number[]) => void
  /** Names for already-selected ids, so badges render before the list loads. */
  knownEvents?: SimpleEvent[]
  /** Load the event list on mount rather than waiting for the popover to open.
   *  Needed when the caller only holds ids and the names have to be resolved. */
  eager?: boolean
  disabled?: boolean
}

const EventFilterPicker: React.FC<EventFilterPickerProps> = ({
  clientId,
  selectedIds,
  onChange,
  knownEvents,
  eager,
  disabled,
}) => {
  const [open, setOpen] = useState(false)
  const [events, setEvents] = useState<SimpleEvent[] | null>(null)
  const [loading, setLoading] = useState(false)

  // Drop the cache when the client changes
  useEffect(() => {
    setEvents(null)
  }, [clientId])

  useEffect(() => {
    if (!(open || eager) || !clientId || events !== null) return
    setLoading(true)
    EventEndpoints.getEventListForProvisioning(clientId)
      .then((list) => setEvents(list as SimpleEvent[]))
      .catch((error) => toast.error(extractErrorMessage(error, 'Failed to load events')))
      .then(() => setLoading(false))
  }, [open, eager, clientId, events])

  const selected = new Set(selectedIds)
  const toggle = (e: SimpleEvent) => {
    if (selected.has(e.id)) {
      onChange(selectedIds.filter((id) => id !== e.id))
    } else {
      onChange([...selectedIds, e.id])
    }
  }

  const nameFor = (id: number) =>
    events?.find((e) => e.id === id)?.name ??
    knownEvents?.find((e) => e.id === id)?.name ??
    `Event #${id}`

  const label =
    selectedIds.length === 0
      ? 'All events'
      : `${selectedIds.length} event${selectedIds.length === 1 ? '' : 's'}`

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || !clientId}
            className="h-8 w-[180px] justify-between"
          >
            <span className="truncate">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2 max-h-80 overflow-y-auto" align="start">
          {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
          {!loading && events && events.length === 0 && (
            <p className="text-sm text-muted-foreground">No events for this client.</p>
          )}
          {!loading && events && events.length > 0 && (
            <div className="space-y-1">
              {events.map((e) => (
                <label
                  key={e.id}
                  className="flex gap-2 items-center py-1 px-1 cursor-pointer hover:bg-accent rounded"
                >
                  <Checkbox
                    checked={selected.has(e.id)}
                    onCheckedChange={() => toggle(e)}
                  />
                  <span className="text-sm">{e.name}</span>
                </label>
              ))}
            </div>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full"
              onClick={() => onChange([])}
            >
              Clear (allow all events)
            </Button>
          )}
        </PopoverContent>
      </Popover>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map((id) => (
            <Badge key={id} variant="secondary" className="text-xs">
              {nameFor(id)}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

const AccessControlTab: React.FC<AccessControlTabProps> = ({
  identity,
  clients,
  onUpdate,
  onDeleteAccess,
  identityId,
  setIdentity,
}) => {
  const [newClientId, setNewClientId] = useState('')
  const [newAccessLevel, setNewAccessLevel] = useState('')
  const [newAllowedEventIds, setNewAllowedEventIds] = useState<number[]>([])
  const [saving, setSaving] = useState(false)

  const provisionings = identity.accessLevelProvisionings || []
  const activeClients = clients.filter((c: any) => !c.dateArchived)

  const handleChangeAccessLevel = async (
    clientId: number,
    accessLevel: string,
    existing: AccessLevelProvisioning,
  ) => {
    try {
      const updated = await IdentityEndpoints.provisionForClient(
        identityId,
        clientId,
        {
          accessLevel: accessLevel as AccessLevel,
          allowedEventIds: (existing.allowedEvents ?? []).map((e) => e.id),
        },
      )
      setIdentity(updated)
      toast.success('Access level updated')
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update access level'))
    }
  }

  const handleChangeEventFilter = async (
    clientId: number,
    accessLevel: AccessLevel,
    allowedEventIds: number[],
  ) => {
    try {
      const updated = await IdentityEndpoints.provisionForClient(
        identityId,
        clientId,
        {
          accessLevel,
          allowedEventIds,
        },
      )
      setIdentity(updated)
      toast.success('Event filter updated')
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update event filter'))
    }
  }

  const handleAddProvisioning = async () => {
    if (!newClientId || !newAccessLevel) return
    setSaving(true)
    try {
      const updated = await IdentityEndpoints.provisionForClient(
        identityId,
        parseInt(newClientId),
        {
          accessLevel: newAccessLevel as AccessLevel,
          allowedEventIds: newAllowedEventIds,
        },
      )
      setIdentity(updated)
      setNewClientId('')
      setNewAccessLevel('')
      setNewAllowedEventIds([])
      toast.success('Access provisioning added')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to add access provisioning'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Login & Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5" />
            Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Login Enabled</Label>
              <Select
                value={identity.ableToLogin ? 'true' : 'false'}
                onValueChange={(v) => onUpdate('ableToLogin', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Administrator</Label>
              <Select
                value={identity.admin ? 'true' : 'false'}
                disabled
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Kiosk Access</Label>
              <Select
                value={identity.kioskAccess ? 'true' : 'false'}
                onValueChange={(v) => onUpdate('kioskAccess', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>POS Config Access</Label>
              <Select
                value={identity.configAccess ? 'true' : 'false'}
                onValueChange={(v) => onUpdate('configAccess', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Apple ID Login</Label>
              <Select
                value={identity.appleIdBehaviour || 'NONE'}
                onValueChange={(v) => onUpdate('appleIdBehaviour', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Disabled</SelectItem>
                  <SelectItem value="OPTIONAL">Optional</SelectItem>
                  <SelectItem value="REQUIRED">Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            <div>
              <Label>POS Config Sections</Label>
              <p className="text-sm text-muted-foreground">
                Settings sections this person can change on the POS.
                {identity.configAccess && ' POS Config Access already includes every section except Maintenance.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {POS_CONFIG_SECTIONS.map((section) => {
                const granted = (identity.configSections || []).includes(section.value)
                const implied = identity.configAccess && section.coveredByConfigAccess
                const id = `posConfigSection-${section.value}`
                return (
                  <div key={section.value} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={granted || implied}
                      disabled={implied}
                      onCheckedChange={(checked) => {
                        const current = identity.configSections || []
                        const next =
                          checked === true
                            ? [...current, section.value]
                            : current.filter((s) => s !== section.value)
                        // Kept in display order; the backend normalizes it the same way.
                        onUpdate(
                          'configSections',
                          POS_CONFIG_SECTIONS.map((s) => s.value)
                            .filter((v) => next.includes(v))
                            .join(',')
                        )
                      }}
                    />
                    <label htmlFor={id} className="text-sm">
                      {section.label}
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* EMS Access */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5" />
            EMS Access
          </CardTitle>
          <CardDescription>Client-level access permissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {provisionings.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Access Level</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {provisionings.map((p) => (
                  <TableRow key={`${p.client.id}-${p.accessLevel}`}>
                    <TableCell className="font-medium">
                      {p.client.name}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={p.accessLevel}
                        onValueChange={(v) => handleChangeAccessLevel(p.client.id, v, p)}
                      >
                        <SelectTrigger className="h-8 w-[280px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(accessLevelLabels).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <EventFilterPicker
                        clientId={p.client.id}
                        selectedIds={(p.allowedEvents ?? []).map((e) => e.id)}
                        knownEvents={p.allowedEvents ?? []}
                        onChange={(ids) =>
                          handleChangeEventFilter(p.client.id, p.accessLevel, ids)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDeleteAccess(p.client.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {provisionings.length === 0 && (
            <p className="text-center py-4 text-sm text-muted-foreground">
              No client access provisioned
            </p>
          )}

          {/* Add new EMS access */}
          <div className="flex gap-2 items-end pt-2 border-t">
            <div className="flex-1">
              <Label className="text-xs">Client</Label>
              <Select
                value={newClientId}
                onValueChange={(v) => {
                  setNewClientId(v)
                  setNewAllowedEventIds([])
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {activeClients.map((c: any) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs">Access Level</Label>
              <Select value={newAccessLevel} onValueChange={setNewAccessLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(accessLevelLabels).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Events</Label>
              <EventFilterPicker
                clientId={newClientId ? parseInt(newClientId) : null}
                selectedIds={newAllowedEventIds}
                onChange={setNewAllowedEventIds}
              />
            </div>
            <Button
              onClick={handleAddProvisioning}
              disabled={saving || !newClientId || !newAccessLevel}
            >
              Add
            </Button>
          </div>

          {/* Access Level Legend */}
          <div className="pt-4 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Access Level Reference
            </p>
            <div className="space-y-1.5">
              {Object.entries(accessLevelLabels).map(([key, label]) => (
                <div key={key} className="flex gap-2 text-xs">
                  <span className="font-medium text-foreground min-w-[180px] shrink-0">
                    {label}
                  </span>
                  <span className="text-muted-foreground">
                    {accessLevelDescriptions[key]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* External Access */}
      <ExternalAccessPanel
        identity={identity}
        identityId={identityId}
        clients={activeClients}
        setIdentity={setIdentity}
      />

      {/* Passkeys */}
      <PasskeyManagementPanel identityId={identityId} />
    </div>
  )
}

// ─── Passkey Management Panel ────────────────────────────────────────────

interface PasskeyCredential {
  id: number
  deviceName: string
  dateCreated: string | number
  lastUsed: string | number | null
}

interface PasskeyManagementPanelProps {
  identityId: number
}

const PasskeyManagementPanel: React.FC<PasskeyManagementPanelProps> = ({
  identityId,
}) => {
  const [credentials, setCredentials] = useState<PasskeyCredential[]>([])
  const [loading, setLoading] = useState(true)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadCredentials = async () => {
    setLoading(true)
    try {
      const data = await IdentityEndpoints.getPasskeyCredentials(identityId)
      setCredentials(data)
    } catch {
      setCredentials([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCredentials()
  }, [identityId])

  const handleDelete = async () => {
    if (deleteId === null) return
    setDeleting(true)
    try {
      await IdentityEndpoints.deletePasskeyCredential(deleteId)
      toast.success('Passkey removed')
      setDeleteId(null)
      loadCredentials()
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to remove passkey'))
    } finally {
      setDeleting(false)
    }
  }

  const formatCredDate = (val: string | number | null) => {
    if (!val) return '-'
    try {
      let d: typeof DateTime.prototype
      if (typeof val === 'number') d = DateTime.fromMillis(val)
      else d = DateTime.fromISO(val)
      return d.isValid ? d.toFormat('MMM dd, yyyy h:mm a') : '-'
    } catch {
      return '-'
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="h-5 w-5" />
            Passkeys
          </CardTitle>
          <CardDescription>
            Registered passkeys for this identity
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : credentials.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No passkeys registered.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Device</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {credentials.map((cred) => (
                  <TableRow key={cred.id}>
                    <TableCell className="font-medium">
                      {cred.deviceName || 'Passkey'}
                    </TableCell>
                    <TableCell>{formatCredDate(cred.dateCreated)}</TableCell>
                    <TableCell>{formatCredDate(cred.lastUsed)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(cred.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteId !== null}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Passkey</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this passkey? The user will no
              longer be able to sign in with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ─── External Access Panel ───────────────────────────────────────────────

interface ExternalAccessPanelProps {
  identity: Identity
  identityId: number
  clients: any[]
  setIdentity: (identity: Identity) => void
}

const ExternalAccessPanel: React.FC<ExternalAccessPanelProps> = ({
  identity,
  identityId,
  clients,
  setIdentity,
}) => {
  const [extClientId, setExtClientId] = useState('')
  const [togglingOp, setTogglingOp] = useState<string | null>(null)
  const [paramDrafts, setParamDrafts] = useState<
    Record<string, Record<string, string>>
  >({})

  const externalAccess = identity.externalAccess || []
  const types = ExternalAccessTypes()

  // Default client
  useEffect(() => {
    if (clients.length > 0 && !extClientId) {
      setExtClientId(clients[0].id.toString())
    }
  }, [clients.length])

  const clientId = extClientId ? parseInt(extClientId) : 0

  // Find an existing provisioning for a given system+operation+client
  const findProvisioning = (systemKey: string, opKey: string) => {
    return externalAccess.find(
      (e) =>
        e.client.id === clientId &&
        e.targetSystem === systemKey &&
        e.operation === opKey
    )
  }

  // Count active provisionings for a system
  const countForSystem = (systemKey: string): number => {
    return externalAccess.filter(
      (e) => e.client.id === clientId && e.targetSystem === systemKey
    ).length
  }

  const handleToggle = async (
    systemKey: string,
    opKey: string,
    params: Record<string, string> = {}
  ) => {
    const existing = findProvisioning(systemKey, opKey)
    const toggleKey = `${systemKey}:${opKey}`
    setTogglingOp(toggleKey)

    try {
      if (existing) {
        const updated =
          await IdentityEndpoints.removeExternalAccessProvisioning(existing.id)
        setIdentity(updated)
        toast.success('Access removed')
      } else {
        // When enabling ALL for a system, remove individual ops for that system
        // When enabling master ALL, remove all individual provisionings for this client
        const toRemove = externalAccess.filter((e) => {
          if (e.client.id !== clientId) return false
          if (systemKey === 'ALL' && opKey === 'ALL') {
            // Master ALL — remove everything else for this client
            return !(e.targetSystem === 'ALL' && e.operation === 'ALL')
          }
          if (opKey === 'ALL') {
            // System-level ALL — remove other ops in this system
            return e.targetSystem === systemKey && e.operation !== 'ALL'
          }
          return false
        })

        // Remove children first
        for (const entry of toRemove) {
          await IdentityEndpoints.removeExternalAccessProvisioning(entry.id)
        }

        // Then grant the ALL
        const updated = await IdentityEndpoints.provisionExternalAccess(
          identityId,
          {
            clientId,
            targetSystem: systemKey,
            operation: opKey,
            parameters: params,
          }
        )
        setIdentity(updated)
        const removedCount = toRemove.length
        toast.success(
          removedCount > 0
            ? `Access granted (${removedCount} individual permission${removedCount !== 1 ? 's' : ''} consolidated)`
            : 'Access granted'
        )
      }
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update access'))
    } finally {
      setTogglingOp(null)
    }
  }

  const handleParamSave = async (systemKey: string, opKey: string) => {
    const draftKey = `${systemKey}:${opKey}`
    const rawParams = paramDrafts[draftKey] || {}
    const params = Object.fromEntries(
      Object.entries(rawParams).filter(([_, v]) => v.trim() !== '')
    )

    // Remove existing then re-add with params
    const existing = findProvisioning(systemKey, opKey)
    setTogglingOp(draftKey)
    try {
      if (existing) {
        await IdentityEndpoints.removeExternalAccessProvisioning(existing.id)
      }
      const updated = await IdentityEndpoints.provisionExternalAccess(
        identityId,
        {
          clientId,
          targetSystem: systemKey,
          operation: opKey,
          parameters: params,
        }
      )
      setIdentity(updated)
      toast.success('Parameters saved')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to save parameters'))
    } finally {
      setTogglingOp(null)
    }
  }

  // Skip "ALL" system from the accordion since it's a meta-system
  const systemKeys = Object.keys(types).filter((k) => k !== 'ALL')

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="h-5 w-5" />
              External Access
            </CardTitle>
            <CardDescription>
              Toggle system access for this identity
            </CardDescription>
          </div>
          <div className="w-[200px]">
            <Select value={extClientId} onValueChange={setExtClientId}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {/* Client access summary */}
        {(() => {
          const byClient = externalAccess.reduce<
            Record<number, { name: string; count: number }>
          >((acc, e) => {
            const cId = e.client.id
            if (!acc[cId]) acc[cId] = { name: e.client.name, count: 0 }
            acc[cId].count++
            return acc
          }, {})
          const entries = Object.values(byClient).sort((a, b) =>
            a.name.localeCompare(b.name)
          )
          if (entries.length === 0) return null
          return (
            <div className="flex flex-wrap items-center gap-2 text-sm pb-2 border-b">
              <span className="text-muted-foreground">
                {externalAccess.length} access
                {externalAccess.length !== 1 ? ' entries' : ' entry'} across:
              </span>
              {entries.map((e) => (
                <Badge
                  key={e.name}
                  variant={
                    byClient[
                      parseInt(
                        Object.keys(byClient).find(
                          (k) => byClient[parseInt(k)].name === e.name
                        )!
                      )
                    ]?.count
                      ? 'outline'
                      : 'secondary'
                  }
                  className={cn(
                    'text-xs cursor-pointer',
                    clients.find(
                      (c: any) =>
                        c.name === e.name && c.id.toString() === extClientId
                    ) && 'border-primary text-primary'
                  )}
                  onClick={() => {
                    const cId = Object.keys(byClient).find(
                      (k) => byClient[parseInt(k)].name === e.name
                    )
                    if (cId) setExtClientId(cId)
                  }}
                >
                  {e.name}: {e.count}
                </Badge>
              ))}
            </div>
          )
        })()}

        {!extClientId ? (
          <p className="text-center py-6 text-sm text-muted-foreground">
            Select a client to manage access
          </p>
        ) : (
          <>
            {/* ALL — full access across all systems */}
            <div className="flex items-center justify-between py-3 px-3 rounded-md border bg-muted/30">
              <div className="flex items-center gap-3">
                <Switch
                  checked={!!findProvisioning('ALL', 'ALL')}
                  disabled={togglingOp === 'ALL:ALL'}
                  onCheckedChange={() => handleToggle('ALL', 'ALL')}
                />
                <div>
                  <span className="text-sm font-medium">Full Access (ALL)</span>
                  <p className="text-xs text-muted-foreground">
                    Grants access to all systems and operations for this client
                  </p>
                </div>
              </div>
              {togglingOp === 'ALL:ALL' && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

          <Accordion type="multiple" className="w-full">
            {systemKeys.map((systemKey) => {
              const system = types[systemKey]
              const opKeys = Object.keys(system.operations)
              const activeCount = countForSystem(systemKey)

              return (
                <AccordionItem key={systemKey} value={systemKey}>
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{system.name}</span>
                      {activeCount > 0 && (
                        <Badge
                          variant="default"
                          className="text-xs px-1.5 py-0"
                        >
                          {activeCount}
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1">
                      {opKeys.map((opKey) => {
                        const op = system.operations[opKey]
                        const existing = findProvisioning(systemKey, opKey)
                        const isOn = !!existing
                        const toggleKey = `${systemKey}:${opKey}`
                        const isToggling = togglingOp === toggleKey
                        const hasParams = Object.keys(op.parameters).length > 0
                        const existingParams: Record<string, string> =
                          existing?.parameters
                            ? Object.fromEntries(
                                Object.entries(existing.parameters).map(
                                  ([k, v]) => [k, v as string]
                                )
                              )
                            : {}

                        return (
                          <div key={opKey}>
                            <div className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50">
                              <div className="flex items-center gap-3 min-w-0">
                                <Switch
                                  checked={isOn}
                                  disabled={isToggling}
                                  onCheckedChange={() => {
                                    if (hasParams && !isOn) {
                                      // For parameterized ops, turn on with empty params first
                                      handleToggle(systemKey, opKey, {})
                                    } else {
                                      handleToggle(systemKey, opKey)
                                    }
                                  }}
                                />
                                <span
                                  className={cn(
                                    'text-sm',
                                    isOn
                                      ? 'text-foreground'
                                      : 'text-muted-foreground'
                                  )}
                                >
                                  {op.name}
                                </span>
                                {isToggling && (
                                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                )}
                              </div>
                              {isOn && Object.keys(existingParams).length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0"
                                >
                                  Filtered
                                </Badge>
                              )}
                            </div>
                            {/* Parameter inputs when operation is on and has params */}
                            {isOn && hasParams && (
                              <div className="ml-14 mr-3 mb-2 p-3 rounded-md bg-muted/30 space-y-2">
                                {Object.entries(op.parameters).map(
                                  ([paramKey, paramDef]: [string, any]) => {
                                    const draftKey = `${systemKey}:${opKey}`
                                    const currentValue =
                                      paramDrafts[draftKey]?.[paramKey] ??
                                      existingParams[paramKey] ??
                                      ''
                                    const setParam = (value: string) =>
                                      setParamDrafts((prev) => ({
                                        ...prev,
                                        [draftKey]: {
                                          ...(prev[draftKey] || existingParams),
                                          [paramKey]: value,
                                        },
                                      }))
                                    return (
                                      <div
                                        key={paramKey}
                                        className="flex items-start gap-2"
                                      >
                                        <Label className="text-xs w-[140px] shrink-0 text-muted-foreground pt-2">
                                          {paramDef.name}
                                        </Label>
                                        {paramDef.picker === 'emsEvents' ? (
                                          <EventFilterPicker
                                            clientId={clientId}
                                            eager
                                            selectedIds={parseEventIds(
                                              currentValue
                                            )}
                                            onChange={(ids) =>
                                              setParam(ids.join(','))
                                            }
                                          />
                                        ) : (
                                          <Input
                                            value={currentValue}
                                            onChange={(e) =>
                                              setParam(e.target.value)
                                            }
                                            placeholder={
                                              paramDef.placeholder || ''
                                            }
                                            className="h-8 text-sm flex-1"
                                          />
                                        )}
                                      </div>
                                    )
                                  }
                                )}
                                <div className="flex justify-end pt-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleParamSave(systemKey, opKey)
                                    }
                                    disabled={
                                      togglingOp === `${systemKey}:${opKey}`
                                    }
                                  >
                                    Save Filters
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )
            })}
          </Accordion>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Attachments Tab ─────────────────────────────────────────────────────

interface AttachmentsTabProps {
  identity: Identity
  setIdentity: (identity: Identity) => void
}

const AttachmentsTab: React.FC<AttachmentsTabProps> = ({
  identity,
  setIdentity,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)

  const attachments = identity.attachments?.attachments || []
  const attachmentLogId = identity.attachments?.id

  const sortedAttachments = [...attachments].sort(
    (a, b) =>
      new Date(b.dateUploaded).getTime() - new Date(a.dateUploaded).getTime()
  )

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !attachmentLogId) return

    setUploading(true)
    try {
      util.FileToBase64(file, async (base64: string) => {
        try {
          const updated = await AttachmentEndpoints.uploadFile(
            attachmentLogId,
            {
              data: base64,
              contentType: file.type,
              name: file.name,
            }
          )
          setIdentity(updated)
          toast.success('File uploaded')
        } catch (err: any) {
          toast.error(extractErrorMessage(err, 'Failed to upload file'))
        } finally {
          setUploading(false)
          if (fileInputRef.current) fileInputRef.current.value = ''
        }
      })
    } catch {
      setUploading(false)
    }
  }

  const handleDownload = async (file: AttachmentFile) => {
    try {
      const response = await AttachmentEndpoints.downloadFile(file.id)
      if (response instanceof Response) {
        const blob = await response.blob()
        util.DownloadBlob(blob, file.name)
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to download file'))
    }
  }

  const handleDelete = async (file: AttachmentFile) => {
    setDeleting(file.id)
    try {
      const updated = await AttachmentEndpoints.discardFile(file.id)
      setIdentity(updated)
      toast.success('File deleted')
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to delete file'))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Upload area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="h-5 w-5" />
            Upload Attachment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {uploading
                ? 'Uploading...'
                : 'Click to browse or drag a file here'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Attachments list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Paperclip className="h-5 w-5" />
            Files
            {attachments.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {attachments.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {sortedAttachments.length === 0 ? (
            <div className="text-center py-12">
              <Paperclip className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                No attachments yet
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Date Uploaded</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAttachments.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        {file.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {(() => {
                        try {
                          return DateTime.fromJSDate(
                            new Date(file.dateUploaded)
                          ).toFormat('MMM d, yyyy h:mm a')
                        } catch {
                          return '-'
                        }
                      })()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {file.uploadedBy
                        ? `${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownload(file)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(file)}
                          disabled={deleting === file.id}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
